import pMemoize from "@-/com.npmjs.p-memoize";
import onetime from "@-/com.npmjs.onetime";
import { Platform } from "@-/com.npmjs.react-native";
import type { ActorRawDatabaseHandle } from "@-/dev.improve.haniverse.database.actor";
import { withDeadline } from "@-/dev.improve.haniverse.lib.deadline";
import { createPlainConsola } from "@-/dev.improve.haniverse.lib.logger";
import {
  getActorOwnerKey,
  getDeviceSetting,
  setDeviceSetting,
} from "@-/dev.improve.haniverse.lib.device-settings";
import {
  countActorDataExportRows,
  prepareActorDataReplacement,
  type ActorDataExport,
} from "@-/dev.improve.haniverse.lib.actor-data-transfer";
import { backupEngine, usesNativeBackupEngine } from "#backup-engine";
import { BackupFailure, toBackupFailure } from "#backup-failure";
import {
  BackupOperationExpiredError,
  createBackupQueue,
  runBackupOperation,
  type BackupOperation,
} from "#backup-operation";
import { executeBackupRestore } from "#execute-backup-restore";
import { isBackupDue, isBackupExpired, type BackupManifest, type BackupReason } from "#backup-types";

/** One small local read (a file stat, or one key of the browser's backup store). */
const LEGACY_RECOVERY_HOLD_READ_DEADLINE_MS = 10_000;
/** Automatic backups retry a failure with exponential backoff, never more than this apart. */
const MAX_AUTOMATIC_RETRY_DELAY_MS = 15 * 60_000;

const getBackupLog = onetime(() => createPlainConsola({ defaults: { tag: "backups" } }));

const getBackupRuntime = onetime(() => ({
  automatic: {
    failures: 0,
    lastSuccess: undefined as number | undefined,
    owner: null as string | null,
    retryAfter: 0,
    running: false,
  },
  controlQueue: createBackupQueue("actor sync/backup control queue"),
  enqueue: createBackupQueue("actor backup queue"),
  /** The newest backup this session saved: never pruned, whatever the clock says. */
  newest: null as string | null,
  pinned: new Set<string>(),
}));

/** The precise failure for anything a backup step threw, including an abandoned operation. */
function backupFailureOf(error: unknown, stage: string): BackupFailure {
  if (error instanceof BackupOperationExpiredError) {
    return new BackupFailure(
      {
        detail: `The backup stopped at "${error.lastProgress ?? "starting"}": no progress for ${Math.round(error.idleTimeoutMs / 1000)} seconds`,
        kind: "timeout",
        stage,
      },
      { cause: error },
    );
  }
  return toBackupFailure(error, stage);
}

function recordFailure(failure: BackupFailure): void {
  setDeviceSetting("localBackupLastFailure", failure.message);
  getBackupLog().warn("local backup failed", {
    code: failure.code,
    detail: failure.detail,
    kind: failure.kind,
    stage: failure.stage,
  });
}

function recordSuccess(manifest: BackupManifest): void {
  setDeviceSetting("localBackupLastFailure", null);
  const runtime = getBackupRuntime();
  runtime.newest = manifest.id;
  if (manifest.sourceUserId === runtime.automatic.owner) {
    runtime.automatic.lastSuccess = manifest.createdAt;
    runtime.automatic.failures = 0;
    runtime.automatic.retryAfter = 0;
  }
}

/** Backup-only queue. A stuck scan must never own the sync/control queue. */
async function exclusively<T>(
  action: (operation: BackupOperation) => Promise<T>,
  operation?: BackupOperation,
): Promise<T> {
  const enqueue = (job: BackupOperation) =>
    getBackupRuntime().enqueue(async () => {
      const execute = () => {
        job.check();
        // Bounded by the job, so the cross-tab Web Lock is released when the job expires
        // instead of being held by a straggler for the rest of this tab's life.
        return job.wait(action(job));
      };
      return Platform.OS === "web"
        ? navigator.locks.request("haniverse-local-backups", { signal: job.signal }, execute)
        : execute();
    }, job);
  return operation === undefined ? runBackupOperation(enqueue) : enqueue(operation);
}

export async function setActorBackupRecoveryHold(
  connection: unknown,
  enabled: boolean,
): Promise<void> {
  if (typeof connection !== "object" || connection === null) {
    return;
  }
  const method: unknown = Reflect.get(connection, "setBackupRecoveryHold");
  if (typeof method === "function") {
    await Reflect.apply(method, connection, [enabled]);
  }
}

const migrateRecoveryHold = pMemoize(async () => {
  if (getDeviceSetting("isBackupRecoveryHoldMigrated")) {
    return;
  }
  // One-time compatibility read, before any new automatic backup starts. Never clear an existing
  // hold if an older build stopped between its two writes. Bounded: this runs before every
  // coordinated sync step, and a rejection is not memoized, so a store that does not answer fails
  // this step and is retried by the next one.
  const legacyHold = await withDeadline(
    { deadline: LEGACY_RECOVERY_HOLD_READ_DEADLINE_MS, operation: "read legacy recovery hold" },
    () => backupEngine.readLegacyRecoveryHold(),
  );
  if (legacyHold === "true") {
    setDeviceSetting("isBackupRecoveryHoldEnabled", true);
  }
  setDeviceSetting("isBackupRecoveryHoldMigrated", true);
});

function controlExclusively<T>(action: (job: BackupOperation) => Promise<T>): Promise<T> {
  return runBackupOperation((job) => getBackupRuntime().controlQueue(() => action(job), job));
}

/**
 * Run a sync step (pull/push) exclusively with restores and account swaps, after checking the
 * recovery hold. The step receives the coordination `operation`: it must `report` progress (each
 * applied batch, each retry) so a long-but-moving sync keeps its turn, while one that stops
 * answering expires after `BACKUP_OPERATION_IDLE_DEADLINE_MS` and the queue moves on.
 */
export async function withActorSyncBackupCoordination<T>(
  connection: unknown,
  action: (operation: BackupOperation) => Promise<T>,
): Promise<T> {
  if (!usesNativeBackupEngine) {
    return exclusively(async (job) => {
      const held = (await job.wait(backupEngine.readLegacyRecoveryHold())) === "true";
      setDeviceSetting("isBackupRecoveryHoldEnabled", held);
      await job.wait(setActorBackupRecoveryHold(connection, held));
      if (held) {
        throw new Error(
          "Cloud sync is paused to protect recovered data. Choose Save or Load in Cloud Save to resume.",
        );
      }
      job.check();
      return action(job);
    });
  }
  await migrateRecoveryHold();
  return controlExclusively(async (job) => {
    const held = getDeviceSetting("isBackupRecoveryHoldEnabled");
    await job.wait(setActorBackupRecoveryHold(connection, held));
    if (held) {
      throw new Error(
        "Cloud sync is paused to protect recovered data. Choose Save or Load in Cloud Save to resume.",
      );
    }
    job.check();
    return action(job);
  });
}

function learningPreferences(): NonNullable<BackupManifest["learningPreferences"]> {
  return {
    introduceNewCardsEnabled: getDeviceSetting("introduceNewCardsEnabled"),
    isAnswerBarHidden: getDeviceSetting("isAnswerBarHidden"),
    isAutoplayEnabled: getDeviceSetting("isAutoplayEnabled"),
    isMuted: getDeviceSetting("isMuted"),
  };
}

/** Save one backup inside a backup job. Null: there is no study database to back up. */
async function saveBackup(
  db: ActorRawDatabaseHandle | null,
  reason: BackupReason,
  job: BackupOperation,
  options: { connectionSlotHeld?: boolean } = {},
): Promise<BackupManifest | null> {
  job.check();
  try {
    const manifest = await backupEngine.create(
      {
        createdAt: Date.now(),
        db,
        learningPreferences: learningPreferences(),
        reason,
        sourceUserId: getActorOwnerKey(),
        ...(options.connectionSlotHeld === true ? { connectionSlotHeld: true } : {}),
      },
      job,
    );
    if (manifest !== null) {
      recordSuccess(manifest);
      await pruneBackups();
    }
    return manifest;
  } catch (error) {
    const failure = backupFailureOf(error, "create");
    recordFailure(failure);
    throw failure;
  }
}

/** Retention runs only after a backup succeeded, so it can never empty the recovery history. */
async function pruneBackups(): Promise<void> {
  const runtime = getBackupRuntime();
  const keep = new Set(runtime.pinned);
  if (runtime.newest !== null) {
    keep.add(runtime.newest);
  }
  try {
    const problems = await backupEngine.prune(Date.now(), keep, {
      deleteLegacyArchives: getDeviceSetting("isBackupRecoveryHoldMigrated"),
    });
    for (const problem of problems) {
      getBackupLog().warn("backup retention", problem);
    }
  } catch (error) {
    // Retention is housekeeping: an expired backup that lingers never fails a new one.
    getBackupLog().warn("backup retention failed", backupFailureOf(error, "prune").message);
  }
}

/** Run a backup job, turning every failure (queue expiry included) into a precise one. */
async function backupJob<T>(
  stage: string,
  action: (job: BackupOperation) => Promise<T>,
  operation?: BackupOperation,
): Promise<T> {
  try {
    return await exclusively(action, operation);
  } catch (error) {
    const failure = backupFailureOf(error, stage);
    // `saveBackup` records its own failures; an abandoned creation job never reached it.
    if (stage === "create" && failure.cause instanceof BackupOperationExpiredError) {
      recordFailure(failure);
    }
    throw failure;
  }
}

export interface ActorBackupListing {
  backups: BackupManifest[];
  /** Saved backups that could not be listed, each naming the backup and the reason. */
  problems: string[];
}

export async function listActorBackups(now = Date.now()): Promise<ActorBackupListing> {
  try {
    const listing = await backupEngine.list();
    return {
      backups: listing.backups
        .filter((manifest) => !isBackupExpired(manifest.createdAt, now))
        .sort((a, b) => b.createdAt - a.createdAt),
      problems: listing.problems,
    };
  } catch (error) {
    throw backupFailureOf(error, "list");
  }
}

async function findBackup(id: string): Promise<BackupManifest> {
  const listing = await backupEngine.list();
  const manifest = listing.backups.find((backup) => backup.id === id);
  if (manifest === undefined) {
    throw new BackupFailure({ detail: "This backup no longer exists", kind: "not-found", stage: "read" });
  }
  if (isBackupExpired(manifest.createdAt, Date.now())) {
    throw new BackupFailure({ detail: "This backup has expired", kind: "not-found", stage: "read" });
  }
  return manifest;
}

/** The last automatic or manual backup attempt's failure, as a precise sentence; null after a success. */
export function getActorBackupError(): string | null {
  return getDeviceSetting("localBackupLastFailure");
}

/** Save a backup now (Settings → Backups → Back Up Now). Fails if there is nothing to back up. */
export async function createActorBackup(
  db: ActorRawDatabaseHandle | null,
  reason: BackupReason = "manual",
  operation?: BackupOperation,
): Promise<BackupManifest> {
  return backupJob(
    "create",
    async (job) => {
      const manifest = await saveBackup(db, reason, job);
      if (manifest === null) {
        const failure = new BackupFailure({
          detail: "There is no study data on this device yet, so there is nothing to back up",
          kind: "source-missing",
          stage: "capture",
        });
        recordFailure(failure);
        throw failure;
      }
      return manifest;
    },
    operation,
  );
}

/**
 * The safety backup before a destructive change (sign-out, import, cloud load). With nothing on
 * the device to protect, there is nothing to save and nothing fails. Rejects with a precise
 * `BackupFailure` only when the device refuses to store the copy.
 */
export async function checkpointActorData(
  db: ActorRawDatabaseHandle | null,
  reason: BackupReason,
): Promise<void> {
  if (!getDeviceSetting("isLocalBackupEnabled")) {
    return;
  }
  await backupJob("create", async (job) => {
    await saveBackup(db, reason, job);
  });
}

/** Hourly backups while the app is open. Failures back off and retry; they never stop for good. */
export const maintainActorBackups = pMemoize(
  async (db: ActorRawDatabaseHandle | null): Promise<void> => {
    const automatic = getBackupRuntime().automatic;
    if (db === null || automatic.running || !getDeviceSetting("isLocalBackupEnabled")) {
      return;
    }
    const owner = getActorOwnerKey();
    if (automatic.owner !== owner) {
      automatic.owner = owner;
      automatic.failures = 0;
      automatic.lastSuccess = undefined;
      automatic.retryAfter = 0;
    }
    const now = Date.now();
    if (now < automatic.retryAfter) {
      return;
    }
    automatic.running = true;
    try {
      if (automatic.lastSuccess === undefined) {
        // First check for this owner: the newest saved backup decides whether one is due. A
        // listing that fails just makes one due; it never silently skips backups.
        const listing = await listActorBackups(now).catch(() => null);
        for (const backup of listing?.backups ?? []) {
          if (backup.sourceUserId === owner && backup.createdAt <= now) {
            automatic.lastSuccess = Math.max(automatic.lastSuccess ?? 0, backup.createdAt);
          }
        }
      }
      if (!isBackupDue(automatic.lastSuccess, Date.now())) {
        return;
      }
      // New backups never touch an older build's archive, so they do not wait on its recovery-hold
      // migration (legacy archives are only deleted once that has run).
      await backupJob("create", async (job) => {
        await saveBackup(db, "hourly", job);
      });
    } catch {
      // Recorded by `saveBackup`/`backupJob`; retry with backoff instead of every minute.
      automatic.failures += 1;
      automatic.retryAfter =
        Date.now() + Math.min(MAX_AUTOMATIC_RETRY_DELAY_MS, 60_000 * 2 ** automatic.failures);
    } finally {
      automatic.running = false;
    }
  },
  { cache: false, cacheKey: () => "backup-maintenance" },
);

export async function exportActorBackup(
  id: string,
  operation?: BackupOperation,
): Promise<ActorDataExport> {
  return backupJob(
    "read",
    async (job) => backupEngine.readExport(await findBackup(id), job),
    operation,
  );
}

export async function restoreActorBackup(
  db: ActorRawDatabaseHandle,
  id: string,
  operation?: BackupOperation,
): Promise<number> {
  return backupJob(
    "restore",
    async (job) => {
      const manifest = await findBackup(id);
      const data = await backupEngine.readExport(manifest, job);
      job.check();
      // The hold is durable and precedes both the checkpoint and transaction. Never automatically
      // upload an older recovered state to the account.
      if (usesNativeBackupEngine) {
        await migrateRecoveryHold();
      } else {
        await backupEngine.writeRecoveryHold(true);
      }
      setDeviceSetting("isBackupRecoveryHoldEnabled", true);
      job.check();
      await setActorBackupRecoveryHold(db.$client, true);
      const runtime = getBackupRuntime();
      // Pinned while the restore runs, so retention cannot remove the copy it may need.
      const checkpoint = { id: null as string | null };
      const saveCheckpoint = async (connectionSlotHeld: boolean) => {
        const saved = await saveBackup(db, "before-restore", job, { connectionSlotHeld });
        if (saved !== null) {
          checkpoint.id = saved.id;
          runtime.pinned.add(saved.id);
        }
      };
      try {
        const statements = prepareActorDataReplacement(db, data);
        job.report({ completed: 0, label: "Restoring learning data", total: 1 });
        if (usesNativeBackupEngine) {
          // Inside the restore's own hold: nothing can be written between the safety backup and
          // the replacement, so there is no "changed in between" to refuse.
          await executeBackupRestore(db.$client, statements, {
            checkpoint: () => saveCheckpoint(true),
          });
        } else {
          await saveCheckpoint(false);
          await executeBackupRestore(db.$client, statements);
        }
        if (manifest.learningPreferences !== undefined) {
          setDeviceSetting(
            "introduceNewCardsEnabled",
            manifest.learningPreferences.introduceNewCardsEnabled,
          );
          setDeviceSetting("isAnswerBarHidden", manifest.learningPreferences.isAnswerBarHidden);
          setDeviceSetting("isAutoplayEnabled", manifest.learningPreferences.isAutoplayEnabled);
          setDeviceSetting("isMuted", manifest.learningPreferences.isMuted);
        }
        return countActorDataExportRows(data);
      } finally {
        if (checkpoint.id !== null) {
          runtime.pinned.delete(checkpoint.id);
        }
      }
    },
    operation,
  );
}

/** Called after explicitly choosing how to resume sync of recovered data. */
export async function resumeActorCloudSync(): Promise<void> {
  if (!usesNativeBackupEngine) {
    await exclusively(async () => {
      await backupEngine.writeRecoveryHold(false);
      setDeviceSetting("isBackupRecoveryHoldEnabled", false);
    });
    return;
  }
  await migrateRecoveryHold();
  await controlExclusively(async () => {
    setDeviceSetting("isBackupRecoveryHoldEnabled", false);
  });
}

/**
 * Run an account swap that replaces the study database (sign-in), with its safety backup taken at
 * the last possible moment. `action` receives `checkpoint`, which it calls inside its exclusive
 * section right before the replacement — on native after closing the old connection, so the copy
 * holds every write. A failed checkpoint (a precise `BackupFailure`) aborts the swap.
 */
export async function withActorBackupCheckpoint<T>(
  db: ActorRawDatabaseHandle | null,
  reason: BackupReason,
  action: (checkpoint: () => Promise<void>) => Promise<T>,
): Promise<T> {
  if (!usesNativeBackupEngine) {
    // Web: the backup reads through the connection, so it runs before the swap inside the same
    // cross-tab backup job that the swap then runs in.
    return backupJob(reason, async (job) => {
      if (getDeviceSetting("isLocalBackupEnabled")) {
        await saveBackup(db, reason, job);
      }
      job.check();
      return action(async () => {});
    });
  }
  return controlExclusively(async (job) => {
    job.check();
    return action(() => checkpointActorData(db, reason));
  });
}
